import { useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import {
  Sparkles, Send, Loader, X, Lock, ChevronRight, MessageSquareText,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// ── Chat IA de Onboarding (Caja) ────────────────────────────────────────
// Conversación libre con el agente NVC para que le explique al usuario
// nuevo qué hace la metodología, qué incluye cada etapa y cómo le aplica
// a SU empresa específicamente.
//
// Reglas:
//   - Límite de MAX_TRIAL_MESSAGES mensajes en trial (10 default)
//   - Counter persiste en profiles.caja_onboarding_messages_used
//   - Mensajes de la sesión persisten en localStorage por consultancyId
//   - Cierre manual marca caja_onboarding_done_at
//   - Al alcanzar el límite: chat se bloquea con CTA de paywall
//
// Reusa el edge function ai-report con mode='chat' + systemPrompt custom.
// ────────────────────────────────────────────────────────────────────────

const MAX_TRIAL_MESSAGES = 10

const ONBOARDING_SYSTEM_PROMPT = `Eres el agente estratégico de Next Valley Consultants (NVC). Tu rol AHORA es darle la bienvenida a un nuevo cliente del producto "Estrategia en una Caja" — un self-serve donde el cliente sigue la metodología NVC guiado por IA.

══════════════════ TU OBJETIVO ══════════════════

En esta conversación de onboarding tienes que:
1. Entender QUÉ TIPO DE EMPRESA tiene (industria, tamaño, problema actual)
2. Explicarle la metodología NVC adaptada a SU caso (no genérica)
3. Convencerlo de empezar con Etapa 0 — Mi Modelo de Negocio

══════════════════ METODOLOGÍA NVC (7 ETAPAS) ══════════════════

* Etapa 0 — Modelo de Negocio: entendimiento profundo de cómo opera hoy
* Etapa 1 — Diagnóstico Estratégico: PESTEL + Porter + Interno + DOFA
* Etapa 2 — Definición Estratégica: misión, visión, objetivos y BSC
* Etapa 3 — Análisis del Mercado: tamaño, productos, canales, competencia
* Etapa 4 — Estrategia por Unidad: posicionamiento, portafolio, precio
* Etapa 5 — Plan de Implementación: pilares, KPIs, actividades, responsables
* Etapa 6 — Plan Corporativo: consolidación final para la junta

══════════════════ REGLAS ══════════════════

1. **Tono**: claro, directo, analítico, centrado en resultados. NO marketing
   speak. NO frases como "excelencia operacional", "cliente en el centro",
   "soluciones integrales". NO emojis salvo casos puntuales útiles.

2. **Brevedad**: respuestas de 80-200 palabras máximo. El usuario está
   leyendo, no quiere ensayos. Si tiene que ser largo, usa bullets.

3. **Personalización**: una vez sepas su industria/problema, conecta cada
   etapa con SU caso específico. Da ejemplos concretos.

4. **Estructura sugerida del diálogo**:
   - Tu PRIMER mensaje: saluda por nombre, preséntate, pregunta 1 cosa
     concreta ("¿qué tipo de empresa tienes y cuál es tu reto principal hoy?")
   - Mensajes 2-4: profundiza sobre la empresa, ofrece insights tempranos
   - Mensajes 5-7: explica metodología adaptada
   - Mensajes 8-10: invita a empezar Etapa 0 con CTA claro

5. **Cierre persuasivo**: cuando el usuario tenga claro qué es NVC, sugiere:
   "Listo, podemos empezar. Cierra este chat y dale al botón 'Empezar — Modelo
   de Negocio'. Esa primera etapa te toma ~30-45 min y al terminar recibes
   un informe ejecutivo gratis hecho por mí."

6. **Honestidad**: si el usuario pregunta si Caja es para él y NO lo es
   (ej: empresa muy chica de 1 persona sin estructura), dilo honestamente
   y sugiere alternativas. NO fuerces la venta.

7. **NO inventes** datos de mercado específicos sin web search. Si te
   pregunta por benchmarks reales, dile que la metodología NVC los va a
   levantar en Etapa 3 con tus propios datos.

Tu primera respuesta debe ser EL PRIMER mensaje que el cliente ve al abrir
el chat. Saluda, preséntate brevemente, y pregunta UNA cosa concreta.`

export default function CajaOnboardingChat({ companyName, industry, onClose, onStartEtapa0 }) {
  const { profile } = useAuth()
  const [messages, setMessages] = useState([]) // [{role, content}]
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [messagesUsed, setMessagesUsed] = useState(profile?.caja_onboarding_messages_used ?? 0)
  const [error, setError]       = useState('')
  const scrollRef               = useRef(null)
  const lsKey = `nvc_onboarding_chat_${profile?.id ?? 'guest'}`

  const remaining = Math.max(0, MAX_TRIAL_MESSAGES - messagesUsed)
  const atLimit   = remaining === 0

  // ── Cargar historial local ─────────────────────────────────────────────
  useEffect(() => {
    if (!profile?.id) return
    try {
      const stored = localStorage.getItem(lsKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed)
          return
        }
      }
    } catch {}
    // Si no hay historial, disparar mensaje inicial del agente
    bootInitialMessage()
  }, [profile?.id])

  // ── Persistir historial local ──────────────────────────────────────────
  useEffect(() => {
    if (messages.length > 0 && profile?.id) {
      localStorage.setItem(lsKey, JSON.stringify(messages))
    }
  }, [messages, profile?.id])

  // ── Auto-scroll al final ───────────────────────────────────────────────
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, loading])

  async function bootInitialMessage() {
    // El primer mensaje del agente lo genera el LLM con contexto
    // mínimo (nombre + industria + empresa)
    const seedUserMsg = `Acabo de entrar al producto. Mi nombre es ${profile?.full_name ?? '(sin nombre)'}. Mi empresa: ${companyName ?? 'no especificada'}. Industria: ${industry ?? 'no especificada'}. Salúdame y pregúntame algo concreto para arrancar.`
    await callAgent([{ role: 'user', content: seedUserMsg }], /* isBootstrap */ true)
  }

  // ── Llamar al agente ───────────────────────────────────────────────────
  async function callAgent(historyForCall, isBootstrap = false) {
    setLoading(true)
    setError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            mode: 'chat',
            systemPrompt: ONBOARDING_SYSTEM_PROMPT,
            messages: historyForCall,
          }),
        }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error ?? 'Error del agente')
      const text = data.report ?? data.content ?? data.text
      if (!text) throw new Error('Respuesta vacía del agente')

      // En bootstrap: mostramos solo la respuesta del agente, no el seed user
      if (isBootstrap) {
        setMessages([{ role: 'assistant', content: text }])
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: text }])
        // Incrementar counter solo cuando es un mensaje real del usuario
        const newCount = messagesUsed + 1
        setMessagesUsed(newCount)
        await supabase.from('profiles')
          .update({ caja_onboarding_messages_used: newCount })
          .eq('id', profile.id)
      }
    } catch (err) {
      setError(err.message ?? 'No se pudo contactar al agente')
    } finally {
      setLoading(false)
    }
  }

  // ── Enviar mensaje del usuario ─────────────────────────────────────────
  async function handleSend() {
    const text = input.trim()
    if (!text || loading || atLimit) return
    const next = [...messages, { role: 'user', content: text }]
    setMessages(next)
    setInput('')
    await callAgent(next)
  }

  // ── Cerrar definitivamente ─────────────────────────────────────────────
  async function handleClose() {
    if (profile?.id) {
      await supabase.from('profiles')
        .update({ caja_onboarding_done_at: new Date().toISOString() })
        .eq('id', profile.id)
    }
    onClose?.()
  }

  return (
    <div className="rounded-2xl border-2 border-indigo-200 bg-white shadow-sm overflow-hidden flex flex-col" style={{ maxHeight: '600px' }}>
      {/* Header */}
      <div className="px-5 py-3.5 bg-gradient-to-r from-brand-800 to-brand-700 text-cream-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Sparkles size={16} className="text-accent-400" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm">Agente NVC · Onboarding</p>
            <p className="text-[11px] text-cream-100/70">
              {atLimit
                ? 'Has usado todos tus mensajes gratis'
                : `${remaining} mensajes gratis restantes`}
            </p>
          </div>
        </div>
        <button
          onClick={handleClose}
          title="Cerrar chat"
          className="text-cream-100/60 hover:text-cream-100 transition-colors p-1.5 hover:bg-white/10 rounded-lg"
        >
          <X size={16} />
        </button>
      </div>

      {/* Mensajes */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-cream-50/40" style={{ minHeight: 240 }}>
        {messages.length === 0 && loading && (
          <div className="flex items-center justify-center py-8 text-gray-400 text-sm gap-2">
            <Loader size={14} className="animate-spin" /> El agente está saludando…
          </div>
        )}
        {messages.map((m, i) => (
          <Message key={i} role={m.role} content={m.content} />
        ))}
        {loading && messages.length > 0 && (
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-brand-800 flex items-center justify-center flex-shrink-0">
              <Sparkles size={12} className="text-accent-400" />
            </div>
            <div className="bg-gray-100 px-3 py-2.5 rounded-2xl rounded-tl-sm text-sm text-gray-500 flex items-center gap-2">
              <Loader size={12} className="animate-spin" /> Pensando…
            </div>
          </div>
        )}
      </div>

      {/* Footer: input o paywall */}
      {atLimit ? (
        <div className="px-5 py-4 bg-amber-50/60 border-t border-amber-200">
          <div className="flex items-start gap-3">
            <Lock size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">
                Has usado tus 10 mensajes gratis del agente
              </p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                Puedes continuar solo con la <strong>Etapa 0 — Modelo de Negocio</strong> sin
                pagar. Si quieres seguir conversando con el agente y desbloquear todas
                las etapas, suscríbete desde el dashboard.
              </p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button
                  onClick={onStartEtapa0}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-brand-800 hover:bg-brand-900 text-white px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Sparkles size={12} /> Empezar Etapa 0
                  <ChevronRight size={12} />
                </button>
                <button
                  onClick={handleClose}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Cerrar chat
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); handleSend() }}
          className="px-4 py-3 border-t border-gray-100 bg-white flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder={loading ? 'El agente está respondiendo…' : 'Escribe tu mensaje…'}
            disabled={loading}
            rows={1}
            className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-300 disabled:bg-gray-50 disabled:text-gray-400"
            style={{ maxHeight: 100 }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="inline-flex items-center justify-center bg-brand-800 hover:bg-brand-900 disabled:opacity-40 disabled:cursor-not-allowed text-white p-2.5 rounded-xl transition-colors"
          >
            {loading ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </form>
      )}

      {error && (
        <div className="px-5 py-2 bg-red-50 border-t border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}
    </div>
  )
}

// ── Burbuja de mensaje ─────────────────────────────────────────────────────────
function Message({ role, content }) {
  const isUser = role === 'user'
  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-accent-500 text-brand-900' : 'bg-brand-800 text-accent-400'
      }`}>
        {isUser
          ? <span className="text-xs font-bold">TÚ</span>
          : <Sparkles size={12} />}
      </div>
      <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
        isUser
          ? 'bg-brand-800 text-cream-100 rounded-tr-sm'
          : 'bg-white border border-gray-100 text-gray-800 rounded-tl-sm shadow-sm'
      }`}>
        {isUser
          ? content
          : <article className="prose prose-sm max-w-none prose-headings:font-semibold prose-p:my-1.5 prose-strong:text-gray-900 prose-li:my-0.5">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </article>}
      </div>
    </div>
  )
}

// ── Trigger compacto (botón para abrir el chat) ────────────────────────────────
export function CajaOnboardingChatTrigger({ onOpen, hasUsedChat }) {
  return (
    <button
      onClick={onOpen}
      className="inline-flex items-center gap-2 bg-white hover:bg-cream-100 border-2 border-brand-800 text-brand-800 font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm"
    >
      <MessageSquareText size={16} />
      {hasUsedChat ? 'Continuar conversación con el agente' : 'Hablar con el agente NVC'}
    </button>
  )
}
